import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import api from '../utils/axios';

interface Rol {
  Id: number;
  Nombre: string;
}

interface Sucursal {
  Id: number;
  Nombre: string;
}

interface Perfil {
  Id: number;
  Nombre: string;
}

interface Empleado {
  Id: number;
  Nombre: string;
  Apellidos: string;
}

interface UserProfile {
  imagen: string | null;
  nombreCompleto: string | null;
  idEmpleado: number | null;
  rol: Rol | null;
  nombreRol: string | null;
  sucursal: Sucursal | null;
  nombreSucursal: string | null;
  perfil: Perfil | null;
  nombrePerfil: string | null;
  empleado: Empleado | null;
}

interface UserProfileContextType {
  userProfile: UserProfile;
  updateUserProfile: (profile: Partial<UserProfile>) => void;
  loadUserProfile: () => Promise<void>;
}

const UserProfileContext = createContext<UserProfileContextType | undefined>(undefined);

interface UserProfileProviderProps {
  children: ReactNode;
}

export const UserProfileProvider: React.FC<UserProfileProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [userProfile, setUserProfile] = useState<UserProfile>({
    imagen: null,
    nombreCompleto: null,
    idEmpleado: null,
    rol: null,
    nombreRol: null,
    sucursal: null,
    nombreSucursal: null,
    perfil: null,
    nombrePerfil: null,
    empleado: null
  });

  const loadUserProfile = async () => {
    if (!user) return;

    try {
      const response = await api.post(`users/findOne/${user.Id}`);
      const data = response.data;     
     
      
      // Construir el nombre completo del empleado
      let nombreCompleto = data.Nombre || 'Usuario';
      if (data.empleado) {
        nombreCompleto = `${data.empleado.Nombre || ''} ${data.empleado.Apellidos || ''}`.trim();
      }
      
      setUserProfile({
        imagen: data.imagen || null,
        nombreCompleto: nombreCompleto,
        idEmpleado: data.IdEmpleado || null,
        rol: data.rol || null,
        nombreRol: data.rol?.Nombre || null,
        sucursal: data.sucursal || null,
        nombreSucursal: data.sucursal?.Nombre || null,
        perfil: data.perfil || null,
        nombrePerfil: data.perfil?.Nombre || null,
        empleado: data.empleado || null
      });      
      
      
    } catch (error) {
      console.error('Error al cargar el perfil:', error);
      
      // Fallback usando datos del user del login
      setUserProfile({
        imagen: null,
        nombreCompleto: user.Nombre || 'Usuario Externo',
        idEmpleado: null,
        rol: null,
        nombreRol: user.Rol || user.Perfil || 'Sin Rol',
        sucursal: null,
        nombreSucursal: 'Sistema POS',
        perfil: null,
        nombrePerfil: user.Perfil || null,
        empleado: null
      });
    }
  };

  const updateUserProfile = (profile: Partial<UserProfile>) => {
    setUserProfile(prev => ({
      ...prev,
      ...profile
    }));
  };

  useEffect(() => {
    loadUserProfile();
  }, [user]);

  return (
    <UserProfileContext.Provider
      value={{
        userProfile,
        updateUserProfile,
        loadUserProfile
      }}
    >
      {children}
    </UserProfileContext.Provider>
  );
};

export const useUserProfile = () => {
  const context = useContext(UserProfileContext);
  if (!context) {
    throw new Error('useUserProfile debe ser usado dentro de un UserProfileProvider');
  }
  return context;
};